import { useState } from "react"
import type * as FirebaseAuth from "firebase/auth"
import { useZodForm } from "popopo-lib-front/zod-form"
import { z } from "zod/v4"
import { LoadingButton, ManagedInput } from "popopo-lib-front/component"
import { Stack, Button } from "react-bootstrap"
import { api, firestore } from "../../core"
import { useToast } from "popopo-lib-front/hook"
import { useUserVrmRegistrations } from "popopo-lib-front/firestore-access"
import { UserDocument } from "popopo-msg"
import { SpinnerRow } from "../SpinnerRow"

export const VrmRegistrationDebugForm = (props: { firebase: FirebaseAuth.User; user: UserDocument }) => {
  const toast = useToast()
  const registrations = useUserVrmRegistrations(firestore, props.user.id, 20)
  const [applyingId, setApplyingId] = useState<string | null>(null)
  const form = useZodForm({
    schema: z.object({
      itemId: z.string().min(1, "itemId は必須です"),
    }),
    defaultValues: {
      itemId: "",
    },
  })
  const { isValid, isSubmitting } = form.formState

  const applyLook = async (vrmRegistrationId: string) => {
    setApplyingId(vrmRegistrationId)
    try {
      const idToken = await props.firebase.getIdToken()
      const res = await api.PUT("/api/v2/users/me/profile/look", {
        body: { kind: "VC", id: vrmRegistrationId },
        headers: {
          authorization: `Bearer ${idToken}`,
        },
      })
      if (res.error !== undefined) {
        toast.add({
          type: "danger",
          title: "ルック変更に失敗しました",
          message: JSON.stringify(res.error),
        })
        return
      }
      toast.add({
        type: "success",
        title: "ルック変更",
        message: `VC ルックに変更しました (registrationId: ${vrmRegistrationId})`,
      })
    } catch (e) {
      console.error(e)
      toast.add({
        type: "danger",
        title: "ルック変更に失敗しました",
        message: e instanceof Error ? e.message : String(e),
      })
    } finally {
      setApplyingId(null)
    }
  }

  return (
    <Stack gap={3}>
      <form onSubmit={form.handleSubmit(async (data) => {
        try {
          const idToken = await props.firebase.getIdToken()
          const res = await api.PUT("/api/v2/users/me/vrm-registrations", {
            body: { itemId: data.itemId },
            headers: {
              authorization: `Bearer ${idToken}`,
            },
          })
          if (res.data === undefined) {
            toast.add({
              type: "danger",
              title: "VRM 登録に失敗しました",
              message: JSON.stringify(res.error),
            })
            return
          }
          toast.add({
            type: "success",
            title: "VRM 登録",
            message: `vrmRegistrationId: ${res.data.vrmRegistrationId}`,
          })
          form.reset({ itemId: "" })
        } catch (e) {
          console.error(e)
          toast.add({
            type: "danger",
            title: "VRM 登録に失敗しました",
            message: e instanceof Error ? e.message : String(e),
          })
        }
      })}>
        <Stack gap={2}>
          <ManagedInput control={form.control} name="itemId" label="VC itemId" placeholder="VC アイテムの itemId" />
          <LoadingButton isLoading={isSubmitting} isValid={isValid}>
            VRM 登録 (PUT /api/v2/users/me/vrm-registrations)
          </LoadingButton>
        </Stack>
      </form>

      <div>
        <h5>登録済み VRM (users/{props.user.id}/vrm-registrations)</h5>
        {registrations === "loading" ? (
          <SpinnerRow />
        ) : registrations.length === 0 ? (
          <p className="text-muted">登録済みドキュメントはありません</p>
        ) : (
          <ul className="list-group">
            {registrations.map((reg) => (
              <li key={reg.id} className="list-group-item">
                <div><code>{reg.id}</code></div>
                <div>source: <code>{reg.source}</code></div>
                <div>item_id: <code>{reg.item_id}</code></div>
                <div>file_id: <code>{reg.file_id}</code></div>
                <div>name: {reg.name}</div>
                {reg.icon !== undefined && <div>icon: <a href={reg.icon} target="_blank" rel="noreferrer">{reg.icon}</a></div>}
                <div>registered_at: {new Date(reg.registered_at).toLocaleString()}</div>
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="mt-1"
                  disabled={applyingId !== null}
                  onClick={() => applyLook(reg.id)}
                >
                  {applyingId === reg.id ? "適用中..." : "このVRMでルック変更"}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Stack>
  )
}
